import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, HeartHandshake, Sprout, GraduationCap, Users, Accessibility, HeartPulse, HandCoins, Building2, Radio, CheckCircle2, Camera, HandHeart, Users2, FolderCheck } from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import ImpactUpdatedCaption from "@/components/ImpactUpdatedCaption";
import { api, BACKEND_URL } from "@/lib/api";

const mediaUrl = (p) => (p?.startsWith("http") ? p : `${BACKEND_URL}${p}`);

const PROGRAMMES = [
    { icon: HandCoins, title: "Community Livelihoods and Enterprise", body: "Supporting women, youth and community groups with practical funding, skills, market exposure and pathways to sustainable income." },
    { icon: HeartHandshake, title: "Vulnerable Families Support", body: "Targeted food assistance, essential items and referral support for households identified through responsible community engagement." },
    { icon: HandHeart, title: "Humanitarian & Emergency Family Support", body: "Compassionate assistance to families experiencing crisis, including bereavement support for dignified burials, food assistance and referrals — with dignity and respect at the centre." },
    { icon: Sprout, title: "Youth Empowerment and Skills", body: "Skills development, entrepreneurship, career exposure, community participation and positive development initiatives." },
    { icon: Users, title: "Women’s Economic Empowerment", body: "Women-led initiatives, livelihood projects, business development, training and pathways to greater economic independence." },
    { icon: Accessibility, title: "Disability Inclusion", body: "Mobility equipment, humanitarian assistance, referrals, inclusion programmes and improved access to community opportunities." },
    { icon: ShieldCheck, title: "Golden Years Initiative", body: "Support, recognition, wellbeing activities and community assistance for elderly residents." },
    { icon: HeartPulse, title: "Health and Wellbeing", body: "Community health awareness, volunteer health initiatives, maternal wellbeing, medical referrals and practical assistance." },
    { icon: GraduationCap, title: "Education and Child Development", body: "Education assistance, school-related interventions, debate programmes, career exposure and development activities." },
    { icon: Building2, title: "Community Infrastructure", body: "Practical improvements such as water infrastructure repairs, borehole interventions and other community facilities." },
    { icon: Radio, title: "Community Information and Referral", body: "Warren Park Connect organises community information, identifies needs and connects residents with services." },
];

const HERO_IMG = `${BACKEND_URL}/api/media/auth-image-1.png`;
const LISTENING_IMG = `${BACKEND_URL}/api/media/auth-img-6331.webp`;
const ENTERPRISE_IMG = `${BACKEND_URL}/api/media/auth-zimshine-products.jpg`;
const CULTURE_IMG = `${BACKEND_URL}/api/media/auth-img-6685.webp`;

export default function Home() {
    const [stats, setStats] = useState([]);
    const [projects, setProjects] = useState([]);
    useEffect(() => {
        api.get("/public/impact-stats").then(({ data }) => setStats(data.stats || [])).catch(() => {});
        api.get("/public/projects?limit=20").then(({ data }) => setProjects(data.items || [])).catch(() => {});
    }, []);

    const successStory = projects.find((p) => p.slug === "zimshine-community-enterprise") || projects.find((p) => p.is_success_story);
    const listeningStory = projects.find((p) => p.slug === "community-listening-warren-park");
    const galleryProjects = projects.filter((p) => (p.images || []).length > 0).slice(0, 6);

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="home-hero">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Global Seed Foundation team engaging with Warren Park residents including children and elders during an outdoor community listening visit" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f14]/95 via-[#0f2b1d]/85 to-[#0f2b1d]/55" />
                    <div className="absolute inset-0 bg-black/25" />
                </div>
                <div className="relative gsf-container py-24 sm:py-32 lg:py-40">
                    <div className="max-w-3xl text-white fade-in-up">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Global Seed Foundation · Warren Park Connect</div>
                        <h1 className="mt-4 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                            Empowering Communities,<br className="hidden sm:block" /> One Seed at a Time.
                        </h1>
                        <p
                            className="mt-5 text-lg sm:text-xl text-[color:var(--gsf-gold-500)] font-medium leading-snug max-w-2xl"
                            data-testid="hero-supporting-statement"
                        >
                            Growing stronger communities through practical empowerment, dignity and partnership.
                        </p>
                        <p className="mt-4 text-lg text-white/85 max-w-2xl leading-relaxed">
                            Global Seed Foundation strengthens communities through practical empowerment,
                            humanitarian support, inclusive development and meaningful connections between
                            people, skills, services and opportunities.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/impact" className="gsf-btn-gold" data-testid="hero-cta-impact">
                                Explore Our Impact <ArrowRight size={18} />
                            </Link>
                            <Link to="/warren-park-connect" className="gsf-btn-primary bg-white/10 border border-white/20 hover:bg-white/20" data-testid="hero-cta-wpc">
                                Join Warren Park Connect
                            </Link>
                            <Link to="/donate" className="gsf-btn-outline !bg-transparent !text-white !border-white/40 hover:!bg-white hover:!text-[color:var(--gsf-green-700)]" data-testid="hero-cta-donate">
                                Support Our Work
                            </Link>
                            <a
                                href={`${BACKEND_URL}/api/public/impact-snapshot.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="gsf-btn-ghost !text-white hover:!bg-white/10 border border-white/20"
                                data-testid="hero-cta-snapshot"
                            >
                                Download Impact Snapshot (PDF)
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMMUNITY IMPACT — verified statistics first, then descriptive copy */}
            <section className="gsf-section relative overflow-hidden" data-testid="home-community-impact">
                {/* Subtle community-development background pattern */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.05]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 15% 20%, var(--gsf-green-700) 0.5px, transparent 1px), radial-gradient(circle at 85% 60%, var(--gsf-green-700) 0.5px, transparent 1px), radial-gradient(circle at 40% 90%, var(--gsf-gold-500) 0.5px, transparent 1px)",
                        backgroundSize: "48px 48px, 64px 64px, 72px 72px",
                    }}
                />
                <div className="gsf-container relative">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow">Our Community Impact</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Building resilient communities through empowerment, enterprise development, inclusion and sustainable livelihoods.
                        </h2>
                    </div>

                    {/* Verified impact statistics — animated counters */}
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="home-community-stats">
                        {[
                            {
                                key: "families",
                                icon: HandHeart,
                                value: 121,
                                suffix: "+",
                                prefix: "",
                                label: "Vulnerable Families Supported",
                                desc: "Supporting vulnerable households through practical community development initiatives, livelihood support and targeted assistance.",
                            },
                            {
                                key: "youth_women",
                                icon: Users2,
                                value: 31,
                                suffix: "",
                                prefix: "",
                                label: "Youth and Women Empowered",
                                desc: "Women and young people empowered through enterprise development, skills support and income-generating opportunities.",
                            },
                            {
                                key: "projects",
                                icon: FolderCheck,
                                value: 16,
                                suffix: "",
                                prefix: "",
                                label: "Community Projects Funded",
                                desc: "Community-led projects supported through funding, mentorship and ongoing engagement.",
                            },
                            {
                                key: "invested",
                                icon: HandCoins,
                                value: 20000,
                                suffix: "+",
                                prefix: "USD ",
                                label: "Invested in Community Projects",
                                desc: "Direct investment into community enterprise development, vulnerable household support and sustainable livelihood initiatives.",
                            },
                            {
                                key: "mobility",
                                icon: Accessibility,
                                value: 2,
                                suffix: "",
                                prefix: "",
                                label: "Mobility Devices Donated",
                                desc: "Mobility support provided to improve inclusion, independence and dignity for persons with disabilities.",
                            },
                        ].map((s) => {
                            const Icon = s.icon;
                            return (
                                <article
                                    key={s.key}
                                    className="gsf-card p-6 flex flex-col h-full relative overflow-hidden group"
                                    data-testid={`ci-stat-${s.key}`}
                                >
                                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[color:var(--gsf-soft-green)]/60 group-hover:bg-[color:var(--gsf-gold-500)]/10 transition-colors" aria-hidden="true" />
                                    <div className="relative w-11 h-11 rounded-xl bg-[color:var(--gsf-soft-green)] flex items-center justify-center text-[color:var(--gsf-green-700)]">
                                        <Icon size={22} strokeWidth={1.7} />
                                    </div>
                                    <div className="relative mt-5 font-serif text-4xl sm:text-5xl font-bold text-[color:var(--gsf-green-700)] leading-none" data-testid={`ci-stat-${s.key}-value`}>
                                        <AnimatedCounter
                                            value={s.value}
                                            prefix={s.prefix}
                                            suffix={s.suffix}
                                            testId={`ci-stat-${s.key}-counter`}
                                        />
                                    </div>
                                    <h3 className="relative mt-3 font-serif text-base font-semibold text-[color:var(--gsf-charcoal)] leading-snug" data-testid={`ci-stat-${s.key}-label`}>
                                        {s.label}
                                    </h3>
                                    <p className="relative mt-3 text-xs text-[color:var(--gsf-body)] leading-relaxed" data-testid={`ci-stat-${s.key}-desc`}>
                                        {s.desc}
                                    </p>
                                </article>
                            );
                        })}
                    </div>

                    <p className="mt-8 text-sm text-[color:var(--gsf-body)] leading-relaxed max-w-3xl" data-testid="home-community-stats-note">
                        Every statistic represents real people, real communities and measurable progress. Global Seed
                        Foundation is committed to transparent reporting and will continue updating these figures as
                        additional verified projects and beneficiary records are completed.
                    </p>

                    {/* Preserved descriptive copy — now placed AFTER the counters */}
                    <div className="mt-12 max-w-3xl space-y-4 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>Global Seed Foundation believes that lasting community development begins by listening, investing in people and supporting sustainable solutions that create long-term opportunities.</p>
                        <p>Working closely with communities, local leadership and vulnerable households, the Foundation empowers individuals through enterprise development, community participation, inclusion programmes and practical initiatives that improve livelihoods.</p>
                        <p>Rather than providing temporary assistance alone, the Foundation focuses on building self-reliance, dignity and sustainable economic participation.</p>
                    </div>
                </div>
            </section>

            {/* OUR APPROACH — step timeline */}
            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="home-approach">
                <div className="gsf-container">
                    <div className="gsf-eyebrow">Our Approach</div>
                    <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Ten interconnected pathways to community empowerment.</h2>
                    <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="approach-steps">
                        {[
                            { n: "01", t: "Listen", d: "Consultation meetings · disability forums · women's discussions · youth engagement." },
                            { n: "02", t: "Identify Priorities", d: "Communities identify real needs." },
                            { n: "03", t: "Support", d: "Funding · mentorship · training · organisation." },
                            { n: "04", t: "Build", d: "Community enterprises · income generation · skills development." },
                            { n: "05", t: "Grow", d: "Exhibitions · business expansion · community ownership." },
                            { n: "06", t: "Transform", d: "Improved livelihoods · greater independence · community resilience." },
                        ].map((step) => (
                            <li key={step.n} className="gsf-card p-5 relative" data-testid={`approach-${step.n}`}>
                                <div className="text-[10px] font-mono tracking-widest text-[color:var(--gsf-gold-600)]">{step.n}</div>
                                <h3 className="mt-2 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">{step.t}</h3>
                                <p className="mt-2 text-xs text-[color:var(--gsf-body)] leading-relaxed">{step.d}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* INTRO */}
            <section className="gsf-section" data-testid="home-intro">
                <div className="gsf-container grid md:grid-cols-12 gap-12 items-start">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow">About Global Seed Foundation</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            A community-first approach to dignity and opportunity.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>
                            Global Seed Foundation is a community-focused development organisation committed to
                            improving lives through practical, inclusive and sustainable initiatives. Our work begins
                            within Warren Park Constituency and surrounding communities, where we collaborate with
                            residents, volunteers, community leaders, social services teams, development partners and
                            responsible businesses.
                        </p>
                        <p>
                            We support vulnerable families, elderly people, persons with disabilities, women, young
                            people and emerging community enterprises. Our approach combines immediate humanitarian
                            assistance with long-term empowerment, skills development and livelihood opportunities.
                        </p>
                        <div className="flex flex-wrap gap-3 pt-2">
                            <Link to="/about" className="gsf-btn-primary" data-testid="intro-cta-learn">Learn About Us <ArrowRight size={18} /></Link>
                            <Link to="/warren-park-connect" className="gsf-btn-ghost" data-testid="intro-cta-community">Meet Our Community →</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* IMPACT STATS */}
            <section className="bg-[color:var(--gsf-green-900)] text-white gsf-section relative overflow-hidden" data-testid="home-impact-stats">
                <div className="gsf-container">
                    <div className="max-w-2xl">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Our Impact So Far</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold">
                            Practical outcomes, verified over time.
                        </h2>
                        <p className="mt-3 text-white/70 text-sm">
                            Impact figures are updated as projects and verified beneficiary records are consolidated.
                        </p>
                    </div>
                    <ImpactUpdatedCaption stats={stats} tone="dark" testid="home-impact-updated" />
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-6">
                        {stats.map((s) => (
                            <div key={s.key} className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid={`stat-${s.key}`}>
                                <div className="text-4xl sm:text-5xl font-serif font-bold text-[color:var(--gsf-gold-500)]">
                                    <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} testId={`stat-${s.key}-val`} />
                                </div>
                                <div className="mt-3 text-sm text-white/80 leading-snug">{s.title || s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROGRAMMES */}
            <section className="gsf-section" data-testid="home-programmes">
                <div className="gsf-container">
                    <div className="max-w-2xl">
                        <div className="gsf-eyebrow">Our Programmes</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Eleven interconnected pathways to community empowerment.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {PROGRAMMES.map((p, i) => (
                            <div key={p.title} className="gsf-card p-7" data-testid={`programme-card-${i}`}>
                                <div className="w-12 h-12 rounded-xl bg-[color:var(--gsf-soft-green)] flex items-center justify-center text-[color:var(--gsf-green-700)]">
                                    <p.icon size={22} strokeWidth={1.7} />
                                </div>
                                <h3 className="mt-5 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold leading-snug">{p.title}</h3>
                                <p className="mt-3 text-sm text-[color:var(--gsf-body)] leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10">
                        <Link to="/programmes" className="gsf-btn-outline" data-testid="programmes-view-all">View all programmes →</Link>
                    </div>
                </div>
            </section>

            {/* FEATURED STORY — Zimshine, authentic success story */}
            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="home-featured-story">
                <div className="gsf-container grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <figure>
                            <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                                <img
                                    src={successStory?.images?.[0] ? mediaUrl(successStory.images[0]) : ENTERPRISE_IMG}
                                    alt="Members of the Zimshine community enterprise, a Foundation-supported Warren Park initiative, displaying Zimshine-branded detergent products at a public exhibition in Harare"
                                    className="w-full h-full object-cover"
                                    data-testid="featured-story-img"
                                />
                            </div>
                            <figcaption className="mt-3 text-xs text-[color:var(--gsf-body)] italic">
                                Zimshine detergent products displayed at an exhibition in Harare by members of the Foundation-supported community enterprise.
                            </figcaption>
                        </figure>
                    </div>
                    <div className="md:col-span-6">
                        <div className="gsf-eyebrow">Featured Success Story · Zimshine</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            From Community Support to Sustainable Enterprise
                        </h2>
                        <p className="mt-5 text-[color:var(--gsf-body)] leading-relaxed">
                            Zimshine is a Warren Park community enterprise supported by Global Seed Foundation.
                            Established with Foundation funding, skills development and mentorship, Zimshine now
                            manufactures detergent products and receives invitations to exhibitions and public
                            showcases across Harare — a living demonstration of what community empowerment can
                            achieve when funding, skills and organised support meet real community initiative.
                        </p>
                        <Link to="/success-stories/zimshine" className="mt-7 gsf-btn-primary" data-testid="featured-story-cta">
                            Read the Zimshine Story <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* COMMUNITY LISTENING — authentic evidence */}
            <section className="gsf-section" data-testid="home-community-listening">
                <div className="gsf-container grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <div className="gsf-eyebrow">Community Listening</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Every project begins by listening
                        </h2>
                        <p className="mt-5 text-[color:var(--gsf-body)] leading-relaxed">
                            The Foundation begins every intervention by meeting communities where they live.
                            Structured listening sessions bring together residents, elders, women, youth and
                            persons with disabilities to share priorities in their own voices — including
                            dedicated disability inclusion forums that shape our disability programme.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/impact-hub/community-listening-warren-park" className="gsf-btn-outline" data-testid="listening-cta">
                                See the community consultation
                            </Link>
                            <Link to="/impact-hub/disability-inclusion-listening-forum" className="gsf-btn-ghost" data-testid="disability-cta">
                                Disability inclusion forum →
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <figure>
                            <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                                <img
                                    src={listeningStory?.images?.[1] ? mediaUrl(listeningStory.images[1]) : LISTENING_IMG}
                                    alt="Community members including women, children and persons with disabilities gathered indoors for an inclusive listening session in Warren Park"
                                    className="w-full h-full object-cover"
                                    data-testid="listening-img"
                                />
                            </div>
                            <figcaption className="mt-3 text-xs text-[color:var(--gsf-body)] italic">
                                Persons with disabilities and community members participating in an inclusive listening session.
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* IMPACT GALLERY — authentic photo strip pulled from published projects */}
            {galleryProjects.length > 0 && (
                <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="home-impact-gallery">
                    <div className="gsf-container">
                        <div className="flex items-end justify-between flex-wrap gap-4">
                            <div>
                                <div className="gsf-eyebrow">Impact Gallery</div>
                                <h2 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                                    Community empowerment in pictures
                                </h2>
                            </div>
                            <Link to="/gallery" className="gsf-btn-outline text-sm" data-testid="gallery-cta">
                                Browse the full gallery <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="gallery-strip">
                            {galleryProjects.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/impact-hub/${p.slug}`}
                                    className="aspect-square rounded-2xl overflow-hidden group relative"
                                    data-testid={`gallery-tile-${p.slug}`}
                                >
                                    <img src={mediaUrl(p.images[0])} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--gsf-gold-500)] flex items-center gap-1">
                                            {p.verified && <CheckCircle2 size={10} />} <Camera size={10} /> {p.programme}
                                        </div>
                                        <div className="text-xs font-semibold mt-1 line-clamp-2 leading-snug">{p.title}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* WARREN PARK CONNECT FEATURE */}
            <section className="gsf-section" data-testid="home-wpc">
                <div className="gsf-container">
                    <div className="rounded-3xl bg-[color:var(--gsf-green-700)] text-white overflow-hidden">
                        <div className="grid md:grid-cols-2 gap-0">
                            <div className="p-10 sm:p-14">
                                <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Warren Park Connect</div>
                                <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold">
                                    Connecting Skills, Needs and Opportunities
                                </h2>
                                <p className="mt-5 text-white/80 leading-relaxed">
                                    Warren Park Connect is a community information and referral initiative designed to
                                    help organise community needs, skills, trades, services and support requests. It
                                    enables more responsible programme planning and helps residents connect with
                                    relevant opportunities and service providers.
                                </p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Link to="/warren-park-connect#register-skills" className="gsf-btn-gold" data-testid="wpc-register-skills">Register Your Skills</Link>
                                    <Link to="/warren-park-connect#request-support" className="gsf-btn-outline !border-white/40 !text-white hover:!bg-white hover:!text-[color:var(--gsf-green-700)]" data-testid="wpc-request-support">Request Community Support</Link>
                                    <Link to="/get-involved#volunteer" className="gsf-btn-ghost !text-white hover:!bg-white/10" data-testid="wpc-volunteer">Volunteer With Us →</Link>
                                </div>
                            </div>
                            <div className="relative min-h-[280px]">
                                <img src={CULTURE_IMG} alt="Community traditional drum performance during a cultural heritage programme in Harare" className="absolute inset-0 w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* DONATION CTA */}
            <section className="gsf-section" data-testid="home-donation-cta">
                <div className="gsf-container grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <div className="gsf-eyebrow">Support Our Work</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Help Us Grow the Next Opportunity
                        </h2>
                        <p className="mt-5 text-[color:var(--gsf-body)] leading-relaxed max-w-xl">
                            Your support helps Global Seed Foundation deliver humanitarian assistance, community
                            empowerment programmes, disability support, livelihood initiatives and practical
                            development projects.
                        </p>
                    </div>
                    <div className="md:col-span-5 flex md:justify-end gap-3 flex-wrap">
                        <Link to="/donate" className="gsf-btn-gold" data-testid="cta-donate-ecocash">Donate via EcoCash</Link>
                        <Link to="/get-involved#partner" className="gsf-btn-outline" data-testid="cta-partner">Partner With Us</Link>
                    </div>
                </div>
            </section>

            {/* NEWSLETTER */}
            <NewsletterInline />
        </div>
    );
}

function NewsletterInline() {
    const [state, setState] = useState({ first_name: "", last_name: "", email: "", country: "", interests: "", consent: false });
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!state.consent) return;
        setBusy(true);
        try {
            await api.post("/forms/submit", {
                kind: "newsletter",
                consent: true,
                fields: {
                    first_name: state.first_name, last_name: state.last_name,
                    email: state.email, country: state.country, interests: state.interests,
                },
            });
            setOk(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Newsletter submission failed:", err?.response?.data?.detail || err?.message);
        } finally { setBusy(false); }
    }

    return (
        <section className="bg-[color:var(--gsf-green-800)] text-white gsf-section" data-testid="home-newsletter">
            <div className="gsf-container grid md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-5">
                    <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Stay Informed</div>
                    <h2 className="mt-3 font-serif text-3xl font-bold">Receive Community Impact Updates</h2>
                    <p className="mt-4 text-white/70 text-sm max-w-md">
                        Occasional updates on programme progress, community stories and opportunities to get involved.
                    </p>
                </div>
                <div className="md:col-span-7">
                    {ok ? (
                        <div className="rounded-2xl bg-white/10 p-6 text-white/90" data-testid="newsletter-thanks">
                            Thank you — your subscription has been received. We’ll be in touch.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input required placeholder="First name" data-testid="nl-first" value={state.first_name} onChange={(e) => setState({ ...state, first_name: e.target.value })} className="rounded-lg px-4 py-3 bg-white/10 border border-white/20 placeholder-white/50 text-white focus:outline-none focus:border-[color:var(--gsf-gold-500)]" />
                            <input required placeholder="Last name" data-testid="nl-last" value={state.last_name} onChange={(e) => setState({ ...state, last_name: e.target.value })} className="rounded-lg px-4 py-3 bg-white/10 border border-white/20 placeholder-white/50 text-white focus:outline-none focus:border-[color:var(--gsf-gold-500)]" />
                            <input required type="email" placeholder="Email address" data-testid="nl-email" value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} className="rounded-lg px-4 py-3 bg-white/10 border border-white/20 placeholder-white/50 text-white focus:outline-none focus:border-[color:var(--gsf-gold-500)]" />
                            <input placeholder="Country" data-testid="nl-country" value={state.country} onChange={(e) => setState({ ...state, country: e.target.value })} className="rounded-lg px-4 py-3 bg-white/10 border border-white/20 placeholder-white/50 text-white focus:outline-none focus:border-[color:var(--gsf-gold-500)]" />
                            <input placeholder="Areas of interest (optional)" data-testid="nl-interests" value={state.interests} onChange={(e) => setState({ ...state, interests: e.target.value })} className="sm:col-span-2 rounded-lg px-4 py-3 bg-white/10 border border-white/20 placeholder-white/50 text-white focus:outline-none focus:border-[color:var(--gsf-gold-500)]" />
                            <label className="sm:col-span-2 flex items-start gap-2 text-xs text-white/70">
                                <input type="checkbox" checked={state.consent} onChange={(e) => setState({ ...state, consent: e.target.checked })} data-testid="nl-consent" className="mt-0.5" />
                                I consent to receive newsletter updates from Global Seed Foundation and I have read the privacy policy.
                            </label>
                            <button type="submit" disabled={busy || !state.consent} data-testid="nl-submit" className="sm:col-span-2 gsf-btn-gold justify-center disabled:opacity-60">
                                {busy ? "Subscribing…" : "Subscribe"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
